import { useEffect } from 'react';
import { Sparkles, ChevronDown } from 'lucide-react';
import { ChatShowcase, EC_GREEN } from '@/components/ai/ChatShowcase';
import { openChatWidget, warmChatWidget } from '@/components/ai/openChatWidget';
import { CoBrandLockup, GREEN_BTN, FeatureSection, HowItWorks, CustomerFirst, AtmBand, HumanFallback } from '@/components/ai/AiSections';
import { TrustStrip, JourneyCards } from '@/components/ai/AiRentway';
import { FleetShowcase } from '@/components/ai/FleetShowcase';

const HERO_GHOST = 'inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-white/40 bg-white/[0.12] px-6 py-3.5 font-heading font-bold text-sm text-white backdrop-blur-sm transition-colors hover:bg-white hover:text-[#0E6B33]';

function Hero() {
  return (
    <section data-testid="ai-hero" className="light-hero relative overflow-hidden bg-[#07140C]">
      <img src="/ai/hero-dubai.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: 'center 58%' }} />
      <div className="absolute inset-0 bg-gradient-to-r from-[#07140C]/90 via-[#07140C]/60 to-[#07140C]/20" aria-hidden="true" />
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#07140C]/80 to-transparent" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
        <div className="lg:col-span-7">
          <CoBrandLockup light />
          <div className="mt-8"><span data-testid="ai-launch-chip" className="inline-flex items-center gap-2 rounded-full bg-white/12 px-3.5 py-1.5 font-heading font-bold text-[11px] tracking-[0.15em] uppercase text-white ring-1 ring-white/20"><Sparkles className="w-3.5 h-3.5 text-[#7CFC9A]" /> Launching at Arabian Travel Market 2026</span></div>
          <h1 className="mt-5 font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.02]">
            Discover what truly <span style={{ color: '#3DDC6E' }}>AI assisted</span> car rental feels like.
          </h1>
          <p className="mt-6 max-w-xl font-body text-base sm:text-lg text-white/80 leading-relaxed">
            Eurogulf Mobility Group puts customers first in everything we build, and we are thrilled to introduce the AI Car Rental Concierge for Europcar Car Rental: Dubai &amp; NE. Tell it about your trip and it finds the car, the location and the price that work for you, in seconds.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button type="button" data-testid="ai-chat-cta" onClick={openChatWidget} className={`${GREEN_BTN} min-h-[48px]`} style={{ backgroundColor: EC_GREEN }}><Sparkles className="w-4 h-4" /> Chat with the AI Concierge</button>
            <a href="#how-it-works" data-testid="ai-how-link" className={HERO_GHOST}>How it works <ChevronDown className="w-4 h-4" /></a>
          </div>
          <p className="mt-5 font-body text-xs text-white/60">Free to use · No sign-up · English &amp; Arabic</p>
        </div>
        <div className="lg:col-span-5"><ChatShowcase /></div>
      </div>
    </section>
  );
}

export function AiCarRentalContent() {
  return (
    <div data-testid="ai-rental-page">
      <Hero />
      <TrustStrip />
      <FeatureSection />
      <FleetShowcase />
      <JourneyCards />
      <div id="how-it-works" className="scroll-mt-24"><HowItWorks /></div>
      <CustomerFirst />
      <AtmBand />
      <HumanFallback />
    </div>
  );
}

export default function AiCarRentalPage() {
  useEffect(() => {
    document.title = 'Discover AI Assisted Car Rental | Eurogulf Mobility Group × Europcar Car Rental';
    warmChatWidget();
  }, []);
  return <AiCarRentalContent />;
}
